import React, {useState} from 'react';
import PopupModal from "../../../../../components/Modal/PopupModal";
import ProductChangeOrderDetailsTimeModal from "./ProductChangeOrderDetailsTimeModal";
import {AvailabilityInfo, OrderType} from "../../../../../services/Api/types";
import {RootState} from "../../../../../../reducers";
import {getStoreConfig} from "../../../../../services/Store/reducer";
import {getSession, selectLocale} from "../../../../../services/Session/reducer";
import {connect} from "react-redux";
import {useTranslation, withTranslation} from "react-i18next";
import PromotionHelper from "../../../../../services/Menu/services/Promotion/PromotionHelper";
import CategoryHelper from "../../../../../services/Menu/services/Category/CategoryHelper";
import AgeRestrictedContext from "../../../../../services/Common/AgeRestrictedContext";
import StoreUtils from "../../../../../services/Store/StoreUtils";
import AvailableHelper from "../../../../../services/Menu/services/Availability/AvailableHelper";
import {useLocation} from "react-router-dom";
import ItemAvailabilityHelper from "../../../../../services/Menu/services/Item/ItemAvailabilityHelper";
import StateItemHelper from "../../../../../services/Menu/services/State/StateItemHelper";
import {
    getAvailabilities,
    getCategories,
    getItemGroups,
    getItems,
    getMenus,
    getModifierGroups,
    getPromotions
} from "../../../../../services/Menu/reducer";
import {getCart} from "../../../../../services/Cart/reducer";
import HoursHelper from "../../../../../services/Menu/services/Availability/HoursHelper";
import DateService from "../../../../../services/Common/Date/DateService";
import {getParsedSessionOrderType} from "../../../../../utils/order";
import MenuEditItemViewHelper from "../../../../../services/Menu/services/ItemView/MenuEditItemViewHelper";
import {anyMenuHasPickDate} from "../../../../../components/Cart/utils";
import ErrorAlertView from "../../../../../components/Common/ErrorAlertView";
import {DateHelper} from "../../../../../services/Common/Date/DateHelper";
import CartChangeOrderDetailsTypesModal
    from "../../../../../components/Cart/components/CartChangeOrderDetailsModal/CartChangeOrderDetailsTypesModal";
import IframeWidget from "../../../../../utils/IframeWidget";
import {useSessionService} from "../../../../../services/Session/sessionService";

interface OwnProps {
    show: boolean;
    onHide: () => void;
    product: Record<string, any>;
    menuId: number | undefined;
    productItemGroup: Record<string, any> | undefined;
    externalProductState?: Record<string, any>;
    onSave: (session: Record<string, any>) => void;
}

const findProductInCategoryItems = (items: Record<string, any>[], product: Record<string, any>) => {
    return items.find((item: Record<string, any>) => {
        if (item.type === "ITEM_GROUP") {
            return item.items?.some((itemItem: Record<string, any>) => itemItem.item_id === product.id) || item.item.id === product.id;
        }

        return item.id === product.id;
    });
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const ProductChangeOrderDetailsModal = ({
                                            show,
                                            onHide,
                                            session,
                                            categories,
                                            product,
                                            menus,
                                            menuId,
                                            externalProductState,
                                            items,
                                            modifierGroups,
                                            config,
                                            promotions,
                                            cart,
                                            itemGroups,
                                            availabilities,
                                            productItemGroup,
                                            locale,
                                            onSave
                                        }: Props) => {
    const location = useLocation();
    const {t} = useTranslation();
    const [loading, setLoading] = useState(false);
    const sessionService = useSessionService();
    const availability = AvailableHelper.getAvailability(availabilities, product.availability_id);
    const [time, setTime] = useState<string | undefined>(session.pickup_at);
    const [currentType, setCurrentType] = useState<OrderType | "">(session.order_type ? session.order_type as OrderType : "");
    const productState = externalProductState ?? StateItemHelper.create(product.id, items, modifierGroups, time);
    const productView = MenuEditItemViewHelper.getItemView(items, modifierGroups, availabilities, productState.item_id, undefined, undefined, 1, time, productState, currentType);
    const categoryIdsFromUrlState = location.state?.categoryIds;
    const categoryIds = categoryIdsFromUrlState ?? [ItemAvailabilityHelper.getCategoryWithNearestAvailableDateForItem(product, categories, menus, {
        ...session,
        pickup_at: time,
        order_type: currentType,
    }, menuId, productItemGroup)?.id].filter(Boolean);
    const categoriesByIds = categoryIds && categoryIds.length > 0 ? categories.filter((c: Record<string, any>) => c.status === 'ENABLED' && categoryIds.includes(c.id)) : undefined;
    let categoryMenus: Record<string, any>[] = [];
    const parsedCurrentType = getParsedSessionOrderType(currentType);
    const isOpen = availability ? HoursHelper.isOpen(availability.hours, DateService.emptyOrNow(time)) && AvailableHelper.isAvailabile(availability, DateService.emptyOrNow(time)) : true;

    const getParsedCategory = () => {
        let parsedCategory: Record<string, any> | undefined = undefined;

        if (!categoriesByIds || categoriesByIds.length === 0) return parsedCategory;

        const availablePromotions = PromotionHelper.getActivePromotionsForCategory(promotions, cart.total);
        parsedCategory = CategoryHelper.getParsedCategories(
            categoriesByIds,
            items,
            availabilities,
            availablePromotions,
            {
                ...session,
                pickup_at: time,
                order_type: currentType,
            },
            cart,
            itemGroups,
            menus,
            AgeRestrictedContext.isAccepted(StoreUtils.isAgeRestrictionDisabled(config))
        )[0];

        if (!parsedCategory || parsedCategory.entities.length === 0) return undefined;

        const menusWithCategory = CategoryHelper.getMenusForCategoryByIds(parsedCategory, menus);
        const itemFromCategoryItems = findProductInCategoryItems(parsedCategory.entities, product);
        const menusWithCategoryForItem = itemFromCategoryItems ? menusWithCategory.filter((menu: Record<string, any>) => itemFromCategoryItems.menuAvailabilitiesIds.includes(menu.availability_id)) : [...menusWithCategory];
        const menuAvailabilities = AvailableHelper.getAvailabilities(availabilities, menusWithCategoryForItem.map((menu: Record<string, any>) => menu.availability_id));
        categoryMenus = [...menusWithCategoryForItem];

        return menuAvailabilities.length > 0 ? parsedCategory : undefined;
    }

    const finalCategory = getParsedCategory();

    const menusWithCategoryForCurrentOrderType = categoryMenus.filter((menu: Record<string, any>) => menu[parsedCurrentType.toLowerCase()] === true);
    const menuAvailabilitiesForCurrentType = AvailableHelper.getAvailabilities(availabilities, menusWithCategoryForCurrentOrderType.map((menu: Record<string, any>) => menu.availability_id));
    const isCategoryAvailableByHoursForCurrentType = HoursHelper.isOpen(menuAvailabilitiesForCurrentType.flatMap((availability: Record<string, any>) => availability.hours), DateService.emptyOrNow(time));
    const isCategoryAvailableByAvailabilityDates = menuAvailabilitiesForCurrentType.some((availability: Record<string, any>) => AvailableHelper.isAvailabile(availability, DateService.emptyOrNow(time)));
    const isCategoryAvailable = isCategoryAvailableByHoursForCurrentType && isCategoryAvailableByAvailabilityDates && menusWithCategoryForCurrentOrderType.length > 0;
    const availableCategoryItems = CategoryHelper.getAvailableCategoryItems(availabilities, time, finalCategory?.entities || []);
    const isProductInAvailableCategoryItems = !!findProductInCategoryItems(availableCategoryItems, product);
    const isCategoryFinallyAvailable = isCategoryAvailable ? isCategoryAvailable && !!availableCategoryItems.length : isCategoryAvailable;
    const isAvailable = isCategoryFinallyAvailable && isOpen && !productView.disabled && isProductInAvailableCategoryItems;
    const itemFromCategoryItems = findProductInCategoryItems(finalCategory?.entities || [], product);
    let productAvailabilityInfos: AvailabilityInfo[] = !itemFromCategoryItems || !finalCategory ? [] : ItemAvailabilityHelper.getItemAvailabilityInfo(itemFromCategoryItems, availability, config, menus, availabilities, finalCategory, time, currentType as OrderType, locale, t);
    const categoryAvailabilityInfos = CategoryHelper.getAvailabilityInfo(categoryMenus, itemFromCategoryItems ? [itemFromCategoryItems] : [], availabilities, config, currentType as OrderType, time, t, locale, true);

    const getFinalAvailabilityInfos = () => {
        if (itemFromCategoryItems?.item_always_available) {
            return categoryAvailabilityInfos;
        }


        return (isCategoryFinallyAvailable && !isProductInAvailableCategoryItems) || (!isCategoryFinallyAvailable && !isProductInAvailableCategoryItems) ?
            productAvailabilityInfos : categoryAvailabilityInfos ?? productAvailabilityInfos;
    };

    const getAvailabilityInfo = (): { text: string; placement: "bottom" | "top"; nearestDate?: Date; } | undefined => {
        if (isAvailable) return undefined;

        const canChangePickupAt = StoreUtils.isPickDateEnabled(config) && anyMenuHasPickDate(categoryMenus, parsedCurrentType)
        const infoForCurrentOrderType = getFinalAvailabilityInfos().find(info => info.includeCurrentType || info.currentType);
        const infoForOtherOrderTypes = getFinalAvailabilityInfos().filter(info => !info.includeCurrentType && !info.currentType);

        if (
            (!infoForCurrentOrderType && infoForOtherOrderTypes.length === 1 && Object.keys(infoForOtherOrderTypes[0]).length === 1)
            || (!infoForOtherOrderTypes && infoForCurrentOrderType && Object.keys(infoForCurrentOrderType).length === 1)
        ) {
            return undefined;
        }

        const orderTypeName = t(`enums.order.type.${currentType}`);

        if (infoForCurrentOrderType && infoForOtherOrderTypes.length > 0 && !infoForCurrentOrderType.isNowAvailable && infoForOtherOrderTypes.every(info => !info.isNowAvailable) && canChangePickupAt) {
            const formattedDate = infoForCurrentOrderType.nearestAvailableDate ? DateHelper.getFormattedNearestDate(DateHelper.format(infoForCurrentOrderType.nearestAvailableDate), t, locale ?? "") : undefined;

            return {
                placement: "top",
                text: formattedDate ?
                    t("modules.product.field.change_pickup_date_to_nearest_available.title", {date: formattedDate}) :
                    t("modules.product.field.change_pickup_date.title"),
                nearestDate: infoForCurrentOrderType.nearestAvailableDate
            };
        }

        if (infoForCurrentOrderType && !infoForCurrentOrderType.isNowAvailable && infoForOtherOrderTypes.some(info => info.isNowAvailable)) {
            const formattedDate = infoForCurrentOrderType.nearestAvailableDate ? DateHelper.getFormattedNearestDate(DateHelper.format(infoForCurrentOrderType.nearestAvailableDate), t, locale ?? "") : undefined;

            return {
                placement: "bottom",
                text: formattedDate ?
                    t("modules.product.field.selected_order_type_not_available_now_with_nearest_available_date.title", {
                        date: formattedDate,
                        orderType: orderTypeName
                    }) :
                    t("modules.product.field.selected_order_type_not_available_now.title", {orderType: orderTypeName})
            }
        }

        if (!infoForCurrentOrderType && infoForOtherOrderTypes.length > 0 && infoForOtherOrderTypes.every(info => !info.isNowAvailable)) {
            const nearestAvailableDate = infoForOtherOrderTypes.find(info => info.nearestAvailableDate)?.nearestAvailableDate;
            const formattedDate = nearestAvailableDate ? DateHelper.getFormattedNearestDate(DateHelper.format(nearestAvailableDate), t, locale ?? "") : undefined;

            return {
                placement: "bottom",
                text: formattedDate ?
                    t("modules.product.field.change_order_type_or_date_with_nearest_available_date.title", {
                        date: formattedDate,
                        orderType: orderTypeName
                    }) :
                    t("modules.product.field.change_order_type_or_date.title", {orderType: orderTypeName})
            }
        }

        if (!infoForCurrentOrderType && infoForOtherOrderTypes.some(info => info.isNowAvailable)) {
            return {
                placement: "bottom",
                text: t("modules.product.field.change_order_type.title", {orderType: orderTypeName})
            }
        }

        if (infoForCurrentOrderType && infoForOtherOrderTypes.length === 0 && !infoForCurrentOrderType.isNowAvailable && canChangePickupAt) {
            const formattedDate = infoForCurrentOrderType.nearestAvailableDate ? DateHelper.getFormattedNearestDate(DateHelper.format(infoForCurrentOrderType.nearestAvailableDate), t, locale ?? "") : undefined;

            return {
                placement: "top",
                text: formattedDate ?
                    t("modules.product.field.change_pickup_date_to_nearest_available.title", {date: formattedDate}) :
                    t("modules.product.field.change_pickup_date.title"),
                nearestDate: infoForCurrentOrderType.nearestAvailableDate,
            };
        }

        if (infoForCurrentOrderType && infoForOtherOrderTypes.length === 0 && infoForCurrentOrderType.nearestAvailableDate && !infoForCurrentOrderType.isNowAvailable && !canChangePickupAt) {
            return {
                placement: "bottom",
                text: t("modules.product.field.not_available_now.title", {date: DateHelper.getFormattedNearestDate(DateHelper.format(infoForCurrentOrderType.nearestAvailableDate), t, locale ?? "")}),
                nearestDate: infoForCurrentOrderType.nearestAvailableDate,
            };
        }

        return undefined;
    }

    const handleSave = async () => {
        setLoading(true);

        try {
            const sessionValue = {
                ...session,
                order_type: currentType,
                pickup_at: time,
            };
            await sessionService.update(sessionValue);
            onSave(sessionValue);
            IframeWidget.onHide(onHide);
        } catch (err) {
            return null;
        }
        setLoading(false);
    }

    const availabilityInfo = getAvailabilityInfo();
    const availabilityInfoWithTopPlacement = availabilityInfo?.placement === "top" ? availabilityInfo.text : undefined;
    const availabilityInfoWithBottomPlacement = availabilityInfo?.placement === "bottom" ? availabilityInfo.text : undefined;

    return (
        <PopupModal
            title=""
            show={show}
            onHide={onHide}
            className="change-order-details-modal"
            scrollable
            bodyContent={
                <>
                    <CartChangeOrderDetailsTypesModal currentType={currentType} setCurrentType={setCurrentType}
                                                      time={time} setTime={setTime} setDineInOption={undefined}/>
                    {availabilityInfoWithTopPlacement && (
                        <ErrorAlertView message={availabilityInfoWithTopPlacement} className="mt-4 mb-0"/>
                    )}
                    <ProductChangeOrderDetailsTimeModal
                        time={time}
                        onChange={setTime}
                        currentType={currentType}
                        availabilityInfo={availabilityInfo}
                        isAvailable={isAvailable}
                    />
                    {availabilityInfoWithBottomPlacement && (
                        <ErrorAlertView message={availabilityInfoWithBottomPlacement} className="mt-2"/>
                    )}
                </>
            }
            primaryButton={
                <button
                    disabled={!isAvailable}
                    className={`btn-spinner btn btn-primary mt-2 w-100 ${loading ? "loading" : ""}`}
                    onClick={handleSave}>
                    <span className="left spinner-border spinner-border-sm"/>
                    {t("common.action.save")}
                </button>
            }
            stickyFooter
        />
    )
}

const mapStateToProps = (state: RootState) => ({
    config: getStoreConfig(state.config),
    session: getSession(state.session),
    itemGroups: getItemGroups(state.menu),
    modifierGroups: getModifierGroups(state.menu),
    menus: getMenus(state.menu),
    items: getItems(state.menu),
    categories: getCategories(state.menu),
    promotions: getPromotions(state.menu),
    cart: getCart(state.cart),
    availabilities: getAvailabilities(state.menu),
    locale: selectLocale(state.session),
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(ProductChangeOrderDetailsModal));